/**
 * RoRo ocean theme — section class tags + canvas bubble system.
 */
(function () {
  const OCEAN_CLASS_MAP = [
    ["main[data-template='index'] .roro-home.roro-hero", "roro-hero-section"],
    ["main[data-template='index'] .roro-section-problem", "roro-problem-section"],
    ["main[data-template='index'] #how-it-works", "roro-how-section"],
    ["main[data-template='index'] #features", "roro-features-section"],
    ["main[data-template='index'] .roro-section-stages", "roro-age-section"],
  ];

  const AMBIENT_COUNT_DEFAULT = 25;
  const AMBIENT_COUNT_REDUCED = 5;
  const SCROLL_NAV_OFFSET = 80;
  const BUBBLE_CANVAS_Z_INDEX = "2147483646";
  /** @type {((x: number, y: number) => void) | null} */
  let spawnBurstBubblesAt = null;
  /* Scoped selectors — avoid [class*="age"] (matches page/image) and [class*="col"] (matches color-scheme). */
  const REVEAL_SELECTORS = [
    "main .shopify-section h1",
    "main .shopify-section h2",
    "main .shopify-section h3",
    'main [class*="card"]',
    'main [class*="step"]',
    'main [class*="feature"]',
    'main [class*="__col"]',
    'main [class*="stage"]',
    'main [class*="badge"]',
    'main [class*="trust"]',
  ].join(", ");

  /**
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function random(min, max) {
    return min + Math.random() * (max - min);
  }

  function tagOceanSections() {
    const main = document.querySelector("main[data-template='index']");
    if (!main) return;

    OCEAN_CLASS_MAP.forEach(([selector, className]) => {
      const section = document.querySelector(selector);
      if (section) section.classList.add(className);
    });
  }

  /**
   * Fallback: inject hero photo only when Liquid did not render an <img>.
   */
  function initHeroBannerImage() {
    const config = document.getElementById("roro-ocean-hero-config");
    const src = config?.dataset.heroBanner;
    if (!src) return;

    const alt = config.dataset.heroBannerAlt || "";

    const desktopFrame = document.querySelector(
      ".roro-hero--banner .roro-hero__visual-frame"
    );
    if (desktopFrame) {
      let img = desktopFrame.querySelector(
        ".roro-hero__visual-img, .roro-hero__hero-photo"
      );
      if (!img) {
        img = document.createElement("img");
        img.className = "roro-hero__visual-img roro-hero__hero-photo";
        img.loading = "eager";
        img.decoding = "async";
        img.width = 1600;
        img.height = 900;
        desktopFrame.insertBefore(img, desktopFrame.firstChild);
        img.src = src;
        img.alt = alt;
      } else if (!img.getAttribute("src") && !img.currentSrc) {
        img.src = src;
        img.alt = alt;
      }
    }

    const mobileSrc = config.dataset.heroBannerMobile || src;
    const mobileRing = document.querySelector(".roro-hero__mobile-visual-ring");
    if (mobileRing) {
      let mobileImg = mobileRing.querySelector(
        ".roro-hero__mobile-visual-img, .roro-hero__hero-photo"
      );
      if (!mobileImg) {
        mobileImg = document.createElement("img");
        mobileImg.className = "roro-hero__mobile-visual-img roro-hero__hero-photo";
        mobileImg.loading = "eager";
        mobileImg.decoding = "async";
        mobileImg.width = 800;
        mobileImg.height = 800;
        mobileRing.appendChild(mobileImg);
        mobileImg.src = mobileSrc;
        mobileImg.alt = alt;
      } else if (!mobileImg.getAttribute("src") && !mobileImg.currentSrc) {
        mobileImg.src = mobileSrc;
        mobileImg.alt = alt;
      }
    }
  }

  /** Remove any coral overlays from a previous theme version. */
  function removeCoralDecorations() {
    document
      .querySelectorAll(".roro-coral-left, .roro-coral-right")
      .forEach((el) => el.remove());
  }

  /**
   * HTML5 canvas ambient + click-burst bubbles (fixed overlay, no DOM bubble nodes).
   */
  function initBubbleCanvas() {
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    if (document.getElementById("roro-bubble-layer")) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const layer = document.createElement("div");
    layer.id = "roro-bubble-layer";
    layer.setAttribute("aria-hidden", "true");
    Object.assign(layer.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: BUBBLE_CANVAS_Z_INDEX,
      isolation: "isolate",
    });

    const canvas = document.createElement("canvas");
    canvas.id = "roro-bubble-canvas";
    Object.assign(canvas.style, {
      display: "block",
      width: "100%",
      height: "100%",
    });
    layer.appendChild(canvas);
    document.documentElement.appendChild(layer);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    /** @type {HTMLCanvasElement} */
    const cvs = canvas;
    let ambientBubbles = [];
    /** @type {Array<{ x: number, y: number, radius: number, vx: number, vy: number, opacity: number, life: number, decay: number }>} */
    let burstBubbles = [];
    let rafId = 0;

    function resizeCanvas() {
      cvs.width = window.innerWidth;
      cvs.height = window.innerHeight;
    }

    /**
     * @param {number} width
     * @param {number} height
     * @param {boolean} [spawnFullHeight]
     */
    function createAmbientBubble(width, height, spawnFullHeight = true) {
      return {
        x: random(0, width),
        y: spawnFullHeight ? random(0, height) : height + 20,
        radius: random(4, 18),
        speed: random(0.4, 1.2),
        opacity: random(0.2, 0.5),
        wobble: random(0, Math.PI * 2),
        wobbleSpeed: random(0.01, 0.03),
        wobbleAmount: random(0.5, 1.5),
      };
    }

    function initAmbientBubbles(count) {
      ambientBubbles = [];
      for (let i = 0; i < count; i += 1) {
        ambientBubbles.push(createAmbientBubble(cvs.width, cvs.height, true));
      }
    }

    /**
     * @param {number} clickX
     * @param {number} clickY
     */
    function spawnBurstBubbles(clickX, clickY) {
      const count = Math.floor(random(8, 13));
      for (let i = 0; i < count; i += 1) {
        burstBubbles.push({
          x: clickX + random(-20, 20),
          y: clickY + random(-20, 20),
          radius: random(3, 12),
          vx: random(-2, 2),
          vy: random(-4, -1),
          opacity: 0.7,
          life: 1,
          decay: random(0.015, 0.025),
        });
      }
    }

    spawnBurstBubblesAt = spawnBurstBubbles;

    /**
     * @param {CanvasRenderingContext2D} context
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {number} opacity
     */
    function drawBubble(context, x, y, radius, opacity) {
      const ringOpacity = Math.min(1, opacity);

      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.strokeStyle = `rgba(13, 54, 106, ${ringOpacity * 0.5})`;
      context.lineWidth = Math.max(1.6, radius * 0.12);
      context.stroke();

      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.strokeStyle = `rgba(255, 255, 255, ${ringOpacity * 0.92})`;
      context.lineWidth = 1.15;
      context.stroke();

      const glintOpacity = Math.min(1, opacity * 1.5);
      const glintX = x - radius * 0.35;
      const glintY = y - radius * 0.35;
      const glintR = Math.max(1.5, radius * 0.25);
      context.beginPath();
      context.arc(glintX, glintY, glintR, Math.PI * 1.1, Math.PI * 1.65);
      context.strokeStyle = `rgba(255, 255, 255, ${glintOpacity})`;
      context.lineWidth = 1;
      context.stroke();
    }

    function updateAmbientBubbles() {
      const { width, height } = cvs;

      ambientBubbles.forEach((bubble) => {
        bubble.y -= bubble.speed;
        bubble.x += Math.sin(bubble.wobble) * bubble.wobbleAmount;
        bubble.wobble += bubble.wobbleSpeed;

        if (bubble.y < -bubble.radius * 2) {
          bubble.y = height + 20;
          bubble.x = random(0, width);
        }
      });
    }

    function updateBurstBubbles() {
      const next = [];

      burstBubbles.forEach((bubble) => {
        bubble.x += bubble.vx;
        bubble.y += bubble.vy;
        bubble.vy += 0.05;
        bubble.life -= bubble.decay;
        bubble.radius *= 0.99;
        bubble.opacity = bubble.life * 0.7;

        if (bubble.life > 0) {
          next.push(bubble);
        }
      });

      burstBubbles = next;
    }

    function animate() {
      ctx.clearRect(0, 0, cvs.width, cvs.height);

      updateAmbientBubbles();
      ambientBubbles.forEach((bubble) => {
        drawBubble(ctx, bubble.x, bubble.y, bubble.radius, bubble.opacity);
      });

      if (!prefersReducedMotion) {
        updateBurstBubbles();
        burstBubbles.forEach((bubble) => {
          drawBubble(ctx, bubble.x, bubble.y, bubble.radius, bubble.opacity);
        });
      }

      rafId = requestAnimationFrame(animate);
    }

    resizeCanvas();
    initAmbientBubbles(
      prefersReducedMotion ? AMBIENT_COUNT_REDUCED : AMBIENT_COUNT_DEFAULT
    );

    /** @param {PointerEvent} e */
    function onDocumentPointerDown(e) {
      if (e.button !== 0) return;
      spawnBurstBubbles(e.clientX, e.clientY);
    }

    if (!prefersReducedMotion) {
      // Capture phase so burst fires before link handlers (e.g. smooth scroll preventDefault).
      document.addEventListener("pointerdown", onDocumentPointerDown, {
        capture: true,
        passive: true,
      });
    }

    window.addEventListener("resize", resizeCanvas);

    rafId = requestAnimationFrame(animate);
  }

  /**
   * @param {string} href
   * @returns {Element | null}
   */
  function resolveHashTarget(href) {
    if (!href || href === "#") return null;

    const id = decodeURIComponent(href.slice(1));
    if (!id) return null;

    const byId = document.getElementById(id);
    if (byId) return byId;

    try {
      return document.querySelector(href);
    } catch {
      return null;
    }
  }

  function initSmoothScroll() {
    document.documentElement.style.scrollBehavior = "smooth";

    if (!document.getElementById("roro-smooth-scroll-styles")) {
      const style = document.createElement("style");
      style.id = "roro-smooth-scroll-styles";
      style.textContent =
        "html { scroll-behavior: smooth; scroll-padding-top: 80px; }";
      document.head.appendChild(style);
    }

    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener("click", (e) => {
        const href = link.getAttribute("href");
        const target = resolveHashTarget(href);
        if (!target) return;

        e.preventDefault();
        spawnBurstBubblesAt?.(e.clientX, e.clientY);
        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          SCROLL_NAV_OFFSET;
        window.scrollTo({ top, behavior: "smooth" });
      });
    });
  }

  /**
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isInHeroSection(el) {
    return Boolean(
      el.closest(".roro-hero, .roro-home.roro-hero, .roro-hero-section")
    );
  }

  /**
   * @param {HTMLElement} el
   * @returns {number}
   */
  function getSiblingStaggerDelay(el) {
    const parent = el.parentElement;
    if (!parent) return 0;
    const index = Array.from(parent.children).indexOf(el);
    return Math.min(Math.max(index, 0) * 0.08, 0.4);
  }

  /**
   * @param {HTMLElement} el
   * @param {{ hidden?: boolean, delay?: number, translateY?: number }} [opts]
   */
  function setRevealBaseStyles(el, opts = {}) {
    const { hidden = true, delay = 0, translateY = 32 } = opts;
    el.style.willChange = "opacity, transform";
    el.style.transition = `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`;

    if (hidden) {
      el.style.opacity = "0";
      el.style.transform = `translateY(${translateY}px)`;
      el.style.pointerEvents = "none";
      el.classList.add("roro-reveal-pending");
      el.classList.remove("roro-reveal-visible");
    } else {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
      el.style.pointerEvents = "";
      el.classList.remove("roro-reveal-pending");
      el.classList.add("roro-reveal-visible");
    }
  }

  /**
   * @param {HTMLElement} el
   */
  function markRevealVisible(el) {
    el.style.opacity = "1";
    el.style.transform = "translateY(0)";
    el.style.willChange = "auto";
    el.style.pointerEvents = "";
    el.classList.remove("roro-reveal-pending");
    el.classList.add("roro-reveal-visible");
  }

  function initHeroEntrance() {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const firstSection = document.querySelector(
      "main .shopify-section:first-child, main .shopify-section:first-of-type"
    );
    const heroRoot =
      firstSection?.querySelector(".roro-hero") ||
      document.querySelector(".roro-hero, .roro-home.roro-hero");
    if (!heroRoot) return;

    const steps = [
      { el: heroRoot.querySelector(".roro-hero__headline, h1"), delay: 0 },
      { el: heroRoot.querySelector(".roro-hero__subheading"), delay: 150 },
      { el: heroRoot.querySelector(".roro-hero__btn-row"), delay: 300 },
      { el: heroRoot.querySelector(".roro-hero__trust"), delay: 450 },
    ].filter((step) => step.el);

    if (!steps.length) return;

    steps.forEach(({ el }) => {
      el.setAttribute("data-roro-hero-reveal", "");
      if (prefersReducedMotion) {
        setRevealBaseStyles(/** @type {HTMLElement} */ (el), { hidden: false });
        return;
      }
      setRevealBaseStyles(/** @type {HTMLElement} */ (el), {
        translateY: 20,
      });
    });

    if (prefersReducedMotion) return;

    window.setTimeout(() => {
      steps.forEach(({ el, delay }) => {
        window.setTimeout(() => {
          requestAnimationFrame(() => {
            markRevealVisible(/** @type {HTMLElement} */ (el));
          });
        }, delay);
      });
    }, 200);
  }

  const WATER_CANVAS_Z_INDEX = "99998";
  const WATER_SIM_SCALE = 0.35;

  /**
   * @param {Element | EventTarget | null} el
   * @returns {boolean}
   */
  function isScrollableTouchTarget(el) {
    let node = el instanceof Element ? el : null;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const oy = style.overflowY;
      const ox = style.overflowX;
      const scrollableOverflow =
        oy === "auto" ||
        oy === "scroll" ||
        ox === "auto" ||
        ox === "scroll";
      if (
        scrollableOverflow &&
        (node.scrollHeight > node.clientHeight ||
          node.scrollWidth > node.clientWidth)
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  /**
   * Full-page fluid water surface (2D height-map wave propagation + refraction render).
   */
  function initWaterCursor() {
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    if (document.getElementById("roro-water-canvas")) {
      return;
    }

    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.id = "roro-water-canvas";
    Object.assign(canvas.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      pointerEvents: "none",
      zIndex: WATER_CANVAS_Z_INDEX,
      opacity: "0.55",
      mixBlendMode: "screen",
    });
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    let simWidth = 0;
    let simHeight = 0;
    /** @type {Float32Array} */
    let bufA;
    /** @type {Float32Array} */
    let bufB;
    /** @type {ImageData} */
    let imageData;

    let mouseX = -999;
    let mouseY = -999;
    let lastMouseX = -999;
    let lastMouseY = -999;
    let isMoving = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let moveTimer = null;
    let touchStartedOnScrollable = false;
    let rafId = 0;

    function initSimulationBuffers() {
      simWidth = Math.max(
        64,
        Math.floor(window.innerWidth * WATER_SIM_SCALE)
      );
      simHeight = Math.max(
        64,
        Math.floor(window.innerHeight * WATER_SIM_SCALE)
      );
      canvas.width = simWidth;
      canvas.height = simHeight;
      bufA = new Float32Array(simWidth * simHeight);
      bufB = new Float32Array(simWidth * simHeight);
      imageData = ctx.createImageData(simWidth, simHeight);
    }

    /**
     * @param {number} cx
     * @param {number} cy
     * @param {number} strength
     * @param {number} radius
     */
    function disturbWater(cx, cy, strength, radius) {
      const ix = Math.floor(cx);
      const iy = Math.floor(cy);
      const r = Math.max(1, Math.floor(radius));
      const r2 = r * r;

      for (let j = -r; j <= r; j += 1) {
        for (let i = -r; i <= r; i += 1) {
          const x = ix + i;
          const y = iy + j;
          if (x <= 0 || x >= simWidth - 1 || y <= 0 || y >= simHeight - 1) {
            continue;
          }
          const dist2 = i * i + j * j;
          if (dist2 > r2) continue;
          const idx = y * simWidth + x;
          const falloff = 1 - dist2 / r2;
          bufA[idx] += strength * falloff;
        }
      }
    }

    /**
     * @param {number} screenX
     * @param {number} screenY
     * @param {number} strength
     * @param {number} radius
     */
    function disturbAtScreen(screenX, screenY, strength, radius) {
      const simX = (screenX / window.innerWidth) * simWidth;
      const simY = (screenY / window.innerHeight) * simHeight;
      disturbWater(simX, simY, strength, radius);
    }

    function propagate() {
      const damping = 0.88;
      const data = imageData.data;
      const w = simWidth;
      const h = simHeight;

      for (let y = 1; y < h - 1; y += 1) {
        for (let x = 1; x < w - 1; x += 1) {
          const i = y * w + x;
          const newVal =
            (bufA[i - 1] + bufA[i + 1] + bufA[i - w] + bufA[i + w]) * 0.5 -
            bufB[i];
          bufB[i] = newVal * damping;

          const dx = bufB[i + 1] - bufB[i - 1];
          const dy = bufB[i + w] - bufB[i - w];
          const intensity = Math.max(-1, Math.min(1, (dx + dy) * 0.3));
          const pixel = i * 4;

          if (intensity > 0) {
            data[pixel] = Math.floor(100 + intensity * 155);
            data[pixel + 1] = Math.floor(200 + intensity * 55);
            data[pixel + 2] = Math.floor(220 + intensity * 35);
            data[pixel + 3] = Math.floor(intensity * 180);
          } else {
            const depth = -intensity;
            data[pixel] = Math.floor(depth * 20);
            data[pixel + 1] = Math.floor(60 + depth * 80);
            data[pixel + 2] = Math.floor(140 + depth * 80);
            data[pixel + 3] = Math.floor(depth * 160);
          }
        }
      }

      const temp = bufA;
      bufA = bufB;
      bufB = temp;

      ctx.putImageData(imageData, 0, 0);
    }

    function applyCursorDisturbance() {
      if (!isMoving || mouseX < 0) return;

      if (lastMouseX < 0 || lastMouseY < 0) {
        disturbAtScreen(mouseX, mouseY, 35, 4);
        return;
      }

      const simX = (mouseX / window.innerWidth) * simWidth;
      const simY = (mouseY / window.innerHeight) * simHeight;
      const speed = Math.hypot(mouseX - lastMouseX, mouseY - lastMouseY);
      const strength = Math.min(speed * 0.7, 80);
      const radius = 2 + Math.min(speed * 0.1, 3);

      disturbWater(simX, simY, strength, radius);
    }

    function animate() {
      applyCursorDisturbance();
      propagate();
      rafId = requestAnimationFrame(animate);
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {boolean} [pinch]
     */
    function onPointerPosition(x, y, pinch = false) {
      lastMouseX = mouseX;
      lastMouseY = mouseY;
      mouseX = x;
      mouseY = y;
      isMoving = true;
      if (moveTimer) clearTimeout(moveTimer);
      moveTimer = setTimeout(() => {
        isMoving = false;
      }, 120);

      if (pinch) {
        disturbAtScreen(x, y, 140, 16);
      }
    }

    /** @param {MouseEvent} e */
    function onDocumentMouseMove(e) {
      onPointerPosition(e.clientX, e.clientY, false);
    }

    document.addEventListener("mousemove", onDocumentMouseMove, {
      passive: true,
    });
    document.addEventListener("mousedown", (e) => {
      const cx = (e.clientX / window.innerWidth) * simWidth;
      const cy = (e.clientY / window.innerHeight) * simHeight;
      disturbWater(cx, cy, 80, 4);
    });

    document.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches.length === 1) {
          const t = e.touches[0];
          const cx = (t.clientX / window.innerWidth) * simWidth;
          const cy = (t.clientY / window.innerHeight) * simHeight;
          disturbWater(cx, cy, 180, 5);
        }
      },
      { passive: true }
    );

    document.addEventListener(
      "touchmove",
      (e) => {
        if (e.touches.length === 1) {
          const t = e.touches[0];
          const cx = (t.clientX / window.innerWidth) * simWidth;
          const cy = (t.clientY / window.innerHeight) * simHeight;
          disturbWater(cx, cy, 60, 3);
        }
      },
      { passive: true }
    );

    function onResize() {
      cancelAnimationFrame(rafId);
      initSimulationBuffers();
      rafId = requestAnimationFrame(animate);
    }

    initSimulationBuffers();
    window.addEventListener("resize", onResize);
    rafId = requestAnimationFrame(animate);
  }

  function initSectionReveal() {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    initHeroEntrance();

    /** @type {Map<HTMLElement, HTMLElement[]>} */
    const revealSections = new Map();
    document.querySelectorAll("main .shopify-section").forEach((sectionEl) => {
      if (!(sectionEl instanceof HTMLElement)) return;
      if (isInHeroSection(sectionEl)) return;

      const items = Array.from(sectionEl.querySelectorAll(REVEAL_SELECTORS)).filter(
        (el) =>
          el instanceof HTMLElement &&
          !isInHeroSection(el) &&
          !el.hasAttribute("data-roro-hero-reveal")
      );

      if (!items.length) return;
      revealSections.set(sectionEl, /** @type {HTMLElement[]} */ (items));
    });

    if (!revealSections.size) return;

    if (prefersReducedMotion) {
      revealSections.forEach((items) => {
        items.forEach((el) => {
          el.style.opacity = "1";
          el.style.transform = "none";
        });
      });
      return;
    }

    revealSections.forEach((items) => {
      items.forEach((el, index) => {
        const delay = Math.min(index * 0.08, 0.56);
        setRevealBaseStyles(el, { delay });
      });
    });

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const sectionEl = entry.target;
          if (!(sectionEl instanceof HTMLElement)) return;
          const items = revealSections.get(sectionEl);
          if (!items) return;

          items.forEach((el) => {
            markRevealVisible(el);
          });
          sectionObserver.unobserve(sectionEl);
        });
      },
      {
        threshold: [0, 0.12],
        rootMargin: "0px 0px -120px 0px",
      }
    );

    revealSections.forEach((_, sectionEl) => sectionObserver.observe(sectionEl));
  }

  function initOceanTheme() {
    removeCoralDecorations();
    tagOceanSections();
    initHeroBannerImage();
    initBubbleCanvas();
    initSmoothScroll();
    initSectionReveal();
    initWaterCursor();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initOceanTheme);
  } else {
    initOceanTheme();
  }
})();
